/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaWorkspaceContextMenu.ts
 * FILE_ID: JOBHUB-QA-WORKSPACE-CTX-001
 * PURPOSE: Wide-screen right-click on a Build check, area, or checklist.
 */
import { useEffect } from 'react'
import { hasQaItemMenuOpener, openQaItemMenu } from './qaMenuBridge'

type Point = { clientX: number; clientY: number }

export function useQaWorkspaceContextMenu({
  enabled,
  wideScreen,
  onArea,
  onChecklist,
}: {
  enabled: boolean
  wideScreen: boolean
  onArea: (args: { id: string; name: string; point: Point }) => void
  onChecklist: (args: { id: string; name: string; areaId: string; point: Point }) => void
}) {
  useEffect(() => {
    if (!enabled) return
    function handler(event: MouseEvent) {
      if (event.shiftKey) return
      const target = event.target
      if (!(target instanceof Element) || !target.closest('[data-qa-workspace]')) return
      if (target.closest('[role="dialog"]')) return
      const point = { clientX: event.clientX, clientY: event.clientY }
      const item = target.closest('[data-qa-item-row]')
      if (item && hasQaItemMenuOpener()) {
        event.preventDefault()
        event.stopPropagation()
        openQaItemMenu(item.getAttribute('data-qa-item-row') ?? '', point)
        return
      }
      if (!wideScreen) return
      const checklist = target.closest('[data-qa-checklist-row]')
      if (checklist) {
        event.preventDefault()
        event.stopPropagation()
        onChecklist({
          id: checklist.getAttribute('data-qa-checklist-row') ?? '',
          name: checklist.getAttribute('data-qa-checklist-name') ?? '',
          areaId: checklist.getAttribute('data-qa-checklist-area') ?? '',
          point,
        })
        return
      }
      const area = target.closest('[data-qa-area-row]')
      if (area) {
        event.preventDefault()
        event.stopPropagation()
        onArea({
          id: area.getAttribute('data-qa-area-row') ?? '',
          name: area.getAttribute('data-qa-area-name') ?? '',
          point,
        })
      }
    }
    // Reused from: packages/job-hub/src/jobs-list/useJobsTrackerSelection.ts (document contextmenu capture)
    document.addEventListener('contextmenu', handler, true)
    return () => document.removeEventListener('contextmenu', handler, true)
  }, [enabled, wideScreen, onArea, onChecklist])
}
